/**
 * SuccessCelebration — Phase 13 Peak-End Rule component.
 *
 * Three levels for end-of-task positive moments:
 *   1. micro     — in-place button state swap (caller uses useCelebrate()).
 *   2. standard  — bottom success toast (auto-dismiss, dismissible, action).
 *   3. major     — full-screen burst + confetti + big message (2s).
 *
 * Usage:
 *   // Wrap the app once:
 *   <SuccessCelebrationProvider>...</SuccessCelebrationProvider>
 *
 *   // Anywhere inside:
 *   const { celebrate } = useCelebrate();
 *   celebrate({
 *     level: 'standard',
 *     title: 'تم إنشاء الفاتورة INV-0001 بنجاح',
 *     action: { label: 'عرض الفاتورة', onClick: () => nav('/invoices/' + id) },
 *   });
 *
 * All motion is gated by `prefers-reduced-motion: reduce`; reduced mode
 * shows a static banner with no confetti / no scaling animations.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CheckCircle, X } from 'lucide-react';
import { usePrefersReducedMotion } from '../../design-system/hooks/usePrefersReducedMotion';

// ─── Context ──────────────────────────────────────────────────────────────

const CelebrateContext = createContext(null);

export function useCelebrate() {
  const ctx = useContext(CelebrateContext);
  if (!ctx) {
    // Safe fallback: no-op when provider is missing so unit tests and
    // orphan renders do not crash.
    return { celebrate: () => {} };
  }
  return ctx;
}

// ─── CSS injection (once) ────────────────────────────────────────────────

const STYLE_ID = 'success-celebration-styles';
const STYLE_CSS = `
@keyframes sc-toast-in {
  0% { opacity: 0; transform: translate(-50%, 24px); }
  100% { opacity: 1; transform: translate(-50%, 0); }
}
@keyframes sc-toast-out {
  0% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, 16px); }
}
@keyframes sc-check-draw {
  0% { stroke-dashoffset: 36; }
  100% { stroke-dashoffset: 0; }
}
@keyframes sc-icon-pop {
  0% { transform: scale(0); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); }
}
@keyframes sc-major-in {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.92); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}
@keyframes sc-confetti-fall {
  0% {
    opacity: 1;
    transform: translate3d(0, -16px, 0) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: translate3d(var(--sc-dx, 0), 180px, 0) rotate(540deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .sc-toast, .sc-major, .sc-icon, .sc-check-path, .sc-confetti-piece {
    animation: none !important;
    transition: none !important;
  }
}
`;

function ensureStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLE_CSS;
  document.head.appendChild(el);
}

// ─── Icon with animated checkmark ────────────────────────────────────────

const AnimatedCheck = ({ reduced, size = 22 }) => (
  <span
    className="sc-icon inline-flex items-center justify-center rounded-full bg-success-100 text-success-700"
    style={{
      width: size + 8,
      height: size + 8,
      animation: reduced ? 'none' : 'sc-icon-pop 320ms ease-out',
    }}
    aria-hidden="true"
  >
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d="M5 12.5 10 17 19 7.5"
        className="sc-check-path"
        style={{
          strokeDasharray: 36,
          strokeDashoffset: reduced ? 0 : 36,
          animation: reduced ? 'none' : 'sc-check-draw 420ms ease-out forwards 80ms',
        }}
      />
    </svg>
  </span>
);

// ─── Confetti (CSS-only, 8 pieces) ───────────────────────────────────────

const CONFETTI_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

const Confetti = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => ({
        left: 10 + i * 10,
        dx: (i % 2 === 0 ? -1 : 1) * (20 + (i % 3) * 18),
        delay: (i % 4) * 60,
        duration: 1600 + (i % 3) * 200,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="sc-confetti-piece absolute block rounded-sm"
          style={{
            left: `${p.left}%`,
            top: '-12px',
            width: 8,
            height: 14,
            background: p.color,
            ['--sc-dx']: `${p.dx}px`,
            animation: `sc-confetti-fall ${p.duration}ms ease-in ${p.delay}ms forwards`,
          }}
        />
      ))}
    </div>
  );
};

// ─── Standard toast ──────────────────────────────────────────────────────

function StandardToast({ toast, reduced, onDismiss }) {
  const { title, description, action } = toast;
  const [leaving, setLeaving] = useState(false);

  const dismiss = useCallback(() => {
    if (reduced) {
      onDismiss();
      return;
    }
    setLeaving(true);
    window.setTimeout(onDismiss, 200);
  }, [onDismiss, reduced]);

  useEffect(() => {
    const t = window.setTimeout(dismiss, toast.duration ?? 5000);
    return () => window.clearTimeout(t);
  }, [dismiss, toast.duration]);

  const anim = reduced
    ? 'none'
    : leaving
    ? 'sc-toast-out 200ms ease-in forwards'
    : 'sc-toast-in 260ms cubic-bezier(0.3,0,0,1) forwards';

  return (
    <div
      role="status"
      aria-live="polite"
      dir="rtl"
      className="sc-toast fixed bottom-6 start-1/2 z-[120] flex min-w-[320px] max-w-[440px] items-start gap-3 rounded-2xl border border-success-500 bg-success-50 p-4 shadow-lg shadow-success-500/20 dark:bg-success-50"
      style={{ transform: 'translate(-50%, 24px)', animation: anim }}
    >
      <AnimatedCheck reduced={reduced} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-success-700 leading-snug">
          {title}
        </p>
        {description && (
          <p className="mt-0.5 text-xs text-success-700/80 leading-snug">{description}</p>
        )}
        {action && (
          <button
            type="button"
            onClick={() => {
              action.onClick?.();
              dismiss();
            }}
            className="mt-2 inline-flex items-center text-xs font-semibold text-primary-700 underline-offset-2 hover:underline"
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="إغلاق"
        className="text-success-700/70 hover:text-success-700 p-1 rounded-md"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

// ─── Major banner ────────────────────────────────────────────────────────

function MajorBanner({ toast, reduced, onDismiss }) {
  const { title, description } = toast;
  useEffect(() => {
    const t = window.setTimeout(onDismiss, toast.duration ?? 2000);
    return () => window.clearTimeout(t);
  }, [onDismiss, toast.duration]);

  return (
    <div
      role="status"
      aria-live="polite"
      dir="rtl"
      className="sc-major fixed start-1/2 top-1/2 z-[130] flex items-center gap-4 rounded-3xl border border-success-500 bg-white px-8 py-6 shadow-2xl shadow-success-500/30 dark:bg-slate-800"
      style={{
        transform: 'translate(-50%, -50%)',
        animation: reduced ? 'none' : 'sc-major-in 360ms cubic-bezier(0.3,0,0,1) forwards',
      }}
    >
      {!reduced && <Confetti />}
      <AnimatedCheck reduced={reduced} size={34} />
      <div className="min-w-0">
        <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{title}</p>
        {description && (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">{description}</p>
        )}
      </div>
    </div>
  );
}

// ─── Provider ────────────────────────────────────────────────────────────

let idCounter = 0;
const nextId = () => ++idCounter;

export function SuccessCelebrationProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [major, setMajor] = useState(null);
  const reduced = usePrefersReducedMotion();
  const mounted = useRef(true);

  useEffect(() => {
    ensureStyles();
    return () => {
      mounted.current = false;
    };
  }, []);

  const push = useCallback((toast) => {
    if (!mounted.current) return;
    setToasts((prev) => [...prev, { ...toast, id: nextId() }]);
  }, []);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const celebrate = useCallback(
    (opts = {}) => {
      const level = opts.level || 'standard';
      const payload = {
        title: opts.title || 'تم بنجاح',
        description: opts.description,
        action: opts.action,
        duration: opts.duration,
      };
      if (level === 'micro') {
        // Pure in-place button swap — no provider-level UI needed.
        return;
      }
      if (level === 'major') {
        setMajor({ ...payload, id: nextId() });
        return;
      }
      // standard
      push(payload);
    },
    [push]
  );

  const value = useMemo(() => ({ celebrate }), [celebrate]);

  return (
    <CelebrateContext.Provider value={value}>
      {children}
      {toasts.map((t) => (
        <StandardToast key={t.id} toast={t} reduced={reduced} onDismiss={() => remove(t.id)} />
      ))}
      {major && (
        <MajorBanner
          key={major.id}
          toast={major}
          reduced={reduced}
          onDismiss={() => setMajor(null)}
        />
      )}
    </CelebrateContext.Provider>
  );
}

// ─── Level-1 micro helper (button state swap) ───────────────────────────
//
// Caller controls a boolean `success` flag for ~1.5s after the action
// resolves. Returns className + label replacement so the button can:
//   - turn green
//   - show a ✓ icon
//   - swap "حفظ" → "تم الحفظ ✓"
// Usage (see CreateInvoicePage.js):
//
//   const micro = useMicroCelebration();
//   onSave: async () => { await save(); micro.fire(); }
//   className: micro.buttonClass('bg-primary-600 hover:bg-primary-700')
//   label: micro.label('حفظ', 'تم الحفظ')

export function useMicroCelebration(durationMs = 1500) {
  const [active, setActive] = useState(false);
  const timerRef = useRef(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    ensureStyles();
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const fire = useCallback(() => {
    setActive(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setActive(false), durationMs);
  }, [durationMs]);

  const buttonClass = useCallback(
    (baseClass = '') => {
      const transition = reduced ? '' : 'transition-colors duration-200';
      return active
        ? `${baseClass} !bg-success-600 hover:!bg-success-600 !text-white ${transition}`
        : `${baseClass} ${transition}`;
    },
    [active, reduced]
  );

  const label = useCallback(
    (idleLabel, successLabel) => (active ? `${successLabel} ✓` : idleLabel),
    [active]
  );

  return { active, fire, buttonClass, label, reduced };
}

// ─── Tiny inline success icon (for ad-hoc use) ──────────────────────────

export const SuccessIcon = ({ size = 18, className = '' }) => (
  <CheckCircle size={size} className={`text-success-600 ${className}`} aria-hidden="true" />
);

export default SuccessCelebrationProvider;
